import React, { useMemo, useState } from "react";
import "./ApplyPosition.css";
import uploadcloud from "../../../../assets/icons/uploadcloud.png";
import RichTextContent, { CmsText } from "../../../RichTextContent/RichTextContent";
import { submitJobApplication } from "../../../../api/jobApplicationsApi";
import { getApiErrorMessage } from "../../../../api/unwrapApiResponse";
import {
  DEFAULT_PHONE_COUNTRY,
  formatPhoneWithDialCode,
  getCountryByCode,
} from "../../../../data/countryPhonePrefixes";
import PhoneCountrySelect from "../../../../components/shared/PhoneCountrySelect/PhoneCountrySelect";
import "../../../../components/shared/PhoneCountrySelect/PhoneCountrySelect.css";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
  privacy: false,
};

const ApplyPosition = ({ job, section }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const formTitle = section?.fields?.apply_form_title ?? "Apply for this job";
  const formSubtitle =
    section?.fields?.apply_form_subtitle ??
    "We'd love to hear from you. Please fill out this form.";

  const selectedCountry = useMemo(
    () => getCountryByCode(phoneCountry),
    [phoneCountry]
  );

  const phonePlaceholder = selectedCountry
    ? `${selectedCountry.dial} (555) 000-0000`
    : "(555) 000-0000";

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }

    if (!form.email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!resume) {
      setError("Please upload your resume as a PDF.");
      return;
    }

    if (!form.privacy) {
      setError("Please agree to the privacy policy.");
      return;
    }

    const payload = new FormData();
    payload.append("positionSlug", job.slug);
    payload.append("positionTitle", job.title || "");
    payload.append("firstName", form.firstName.trim());
    payload.append("lastName", form.lastName.trim());
    payload.append("email", form.email.trim());
    payload.append("phone", formatPhoneWithDialCode(phoneCountry, form.phone));
    payload.append("message", form.message.trim());
    payload.append("agreedToPrivacy", "true");
    payload.append("resume", resume);

    setBusy(true);
    try {
      await submitJobApplication(payload);
      setSuccess(true);
      setForm(EMPTY_FORM);
      setPhoneCountry(DEFAULT_PHONE_COUNTRY);
      setResume(null);
      const fileInput = document.getElementById("apply-resume");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="apply">
      <div className="apply__container">
        <div className="apply__content">
          {job?.intro ? (
            <RichTextContent content={job.intro} className="intro" />
          ) : null}

          {(job?.intro && (job?.role || job?.responsibilities?.length || job?.qualifications)) ? (
            <div className="divider" />
          ) : null}

          {job?.role ? (
            <>
              <h2>The Role</h2>
              <RichTextContent content={job.role} />
            </>
          ) : null}

          {job?.responsibilities_intro || job?.responsibilities?.length ? (
            <>
              <h2>Key Responsibilities</h2>
              {job.responsibilities_intro ? (
                <RichTextContent content={job.responsibilities_intro} />
              ) : null}
              {job.responsibilities?.length ? (
                <ol>
                  {job.responsibilities.map((item) => (
                    <li key={item}>
                      <CmsText content={item} as="span" />
                    </li>
                  ))}
                </ol>
              ) : null}
            </>
          ) : null}

          {job?.qualifications ? (
            <>
              <h2>Qualifications</h2>
              <RichTextContent content={job.qualifications} />
            </>
          ) : null}
        </div>

        <form className="apply__form" onSubmit={handleSubmit} noValidate>
          <CmsText content={formTitle} as="h3" />
          <CmsText content={formSubtitle} as="p" className="apply__form-sub" />

          {success ? (
            <p className="apply__status apply__status--success" role="status">
              Thank you! Your application has been submitted successfully.
            </p>
          ) : null}

          {error ? (
            <p className="apply__status apply__status--error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="apply__form-row">
            <div className="apply__field">
              <label htmlFor="apply-first-name">First name</label>
              <input
                id="apply-first-name"
                name="firstName"
                type="text"
                autoComplete="given-name"
                placeholder="First name"
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
                disabled={busy}
                required
              />
            </div>

            <div className="apply__field">
              <label htmlFor="apply-last-name">Last name</label>
              <input
                id="apply-last-name"
                name="lastName"
                type="text"
                autoComplete="family-name"
                placeholder="Last name"
                value={form.lastName}
                onChange={(e) => setField("lastName", e.target.value)}
                disabled={busy}
                required
              />
            </div>
          </div>

          <div className="apply__field">
            <label htmlFor="apply-email">Email</label>
            <input
              id="apply-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="you@mail.com"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              disabled={busy}
              required
            />
          </div>

          <div className="apply__field">
            <label htmlFor="apply-phone">Phone number</label>
            <div className="apply__phone-field">
              <PhoneCountrySelect
                value={phoneCountry}
                disabled={busy}
                theme="apply"
                id="apply-phone-country"
                onChange={(code) => {
                  setPhoneCountry(code);
                  setError("");
                  setSuccess(false);
                }}
              />
              <input
                id="apply-phone"
                name="phone"
                type="tel"
                autoComplete="tel-national"
                className="apply__phone-input"
                placeholder={phonePlaceholder}
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="apply__field">
            <span className="apply__field-label" id="apply-cv-label">
              Resume/CV
            </span>
            <label className="apply__upload" htmlFor="apply-resume">
              <input
                id="apply-resume"
                name="resume"
                type="file"
                accept=".pdf,application/pdf"
                className="apply__upload-input"
                onChange={(e) => {
                  setResume(e.target.files?.[0] ?? null);
                  setError("");
                  setSuccess(false);
                }}
                disabled={busy}
                required
              />
              <span className="apply__upload-box" aria-hidden="true">
                <img src={uploadcloud} alt="" />
              </span>
              <p className="apply__upload-text">
                <span className="apply__upload-cta">Click to upload</span>{" "}
                <span className="apply__upload-muted">or drag and drop</span>
              </p>
              <small className="apply__upload-hint">
                {resume ? resume.name : "your PDF file."}
              </small>
            </label>
          </div>

          <div className="apply__field">
            <label htmlFor="apply-message">Message</label>
            <textarea
              id="apply-message"
              name="message"
              rows={5}
              placeholder=""
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="apply__checkbox">
            <input
              type="checkbox"
              id="apply-privacy"
              name="privacy"
              checked={form.privacy}
              onChange={(e) => setField("privacy", e.target.checked)}
              disabled={busy}
              required
            />
            <label htmlFor="apply-privacy">
              You agree to our friendly <u>privacy policy</u>.
            </label>
          </div>

          <button
            type="submit"
            className="buttonclip apply__submit"
            disabled={busy}
          >
            {busy ? "Submitting…" : "Apply"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ApplyPosition;
